import { useState, useEffect } from "react";
import { Archive, Mail, BookMarked, ArrowRight, Clock, CheckCircle2, History } from "lucide-react";
import { apiRequest } from "../context/AuthContext";

/**
 * Two-section component shown at the bottom of the Dashboard:
 *
 *  1. Archive Tabs  — one tab per *active* (non-reverted) switch; shows a
 *     read-only data snapshot. No revert action — reverting a switch is
 *     admin-only and happens outside this component.
 *
 *  2. Switch Log    — full chronological timeline of ALL year switches,
 *     including reverted ones, so the player has a permanent audit trail.
 *
 * Returns null only when the player has never performed a year switch.
 */
export default function EnrolmentYearArchiveTabs({ userId, onReverted }) {
  const [allSwitches, setAllSwitches] = useState([]);   // every switch (incl. reverted)
  const [activeId, setActiveId]       = useState(null); // selected archive tab
  const [archive, setArchive]         = useState(null);
  const [loading, setLoading]         = useState(true);

  const fetchSwitches = () => {
    if (!userId) return;
    setLoading(true);
    apiRequest("get", `/players/${userId}/enrolment-year-switches`)
      .then(({ data }) => {
        const all      = data || [];
        const eligible = all.filter(s => !s.reverted);
        setAllSwitches(all);
        if (eligible.length) setActiveId(eligible[0].id);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(fetchSwitches, [userId]);

  useEffect(() => {
    if (!userId || !activeId) { setArchive(null); return; }
    apiRequest("get", `/players/${userId}/enrolment-year-switches/${activeId}/archive`)
      .then(({ data }) => setArchive(data))
      .catch(() => setArchive(null));
  }, [userId, activeId]);

  if (loading || allSwitches.length === 0) return null;

  const eligible = allSwitches.filter(s => !s.reverted);

  // Compute archive snapshot stats
  const today        = new Date().toISOString().slice(0, 10);
  const overdueCount = (archive?.tracked_colleges || []).filter(t => {
    const fu = (t.follow_up_date || "").slice(0, 10);
    return fu && fu < today;
  }).length;
  const contactedCount = (archive?.tracked_colleges || []).filter(t => t.status !== "interested").length;
  const emailCount     = (archive?.emails || []).length;

  // ── Switch log helpers ───────────────────────────────────────────────────
  const fmtDate = iso =>
    iso
      ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
      : "—";

  const statusBadge = s => {
    if (s.reverted) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
          <CheckCircle2 className="w-3 h-3" /> Reverted
        </span>
      );
    }
    if (s.superseded) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
          <Archive className="w-3 h-3" /> Superseded
        </span>
      );
    }
    if (s.revert_eligible) {
      const daysLeft = Math.max(0, Math.ceil(
        (new Date(s.revert_deadline) - Date.now()) / 86_400_000
      ));
      return (
        <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
          <Clock className="w-3 h-3" /> {daysLeft}d to undo
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
        <Archive className="w-3 h-3" /> Permanent
      </span>
    );
  };

  return (
    <div className="space-y-4" data-testid="enrolment-year-archive-tabs">

      {/* ── 1. Archive Tabs (active/non-reverted switches only) ──────────── */}
      {eligible.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {/* Tab bar */}
          <div className="flex border-b border-slate-100 overflow-x-auto">
            {eligible.map(s => (
              <button
                key={s.id}
                data-testid={`archive-tab-btn-${s.id}`}
                onClick={() => setActiveId(s.id)}
                className={`flex-shrink-0 flex items-center gap-2 px-5 py-3 text-xs font-bold uppercase tracking-wide transition-all border-b-2 ${
                  activeId === s.id
                    ? "border-orange-500 text-slate-900"
                    : "border-transparent text-slate-400 hover:text-slate-600"
                }`}
              >
                <Archive className="w-3.5 h-3.5" />
                {s.year_label}
              </button>
            ))}
          </div>

          {archive && (
            <div className="p-5 space-y-4">
              {/* Header: description */}
              <div className="flex items-start justify-between gap-4">
                <p className="text-xs text-slate-400 flex-1">
                  Archived when you switched to{" "}
                  <strong>{archive.switch?.new_target_year}</strong> on{" "}
                  {fmtDate(archive.switch?.switched_at)}.{" "}
                  This is a read-only snapshot.
                  {eligible.find(s => s.id === activeId)?.revert_eligible && (
                    <>
                      {" "}Want this reverted? Email{" "}
                      <a href="mailto:support@getcourtbound.com" className="underline hover:text-slate-600">
                        support@getcourtbound.com
                      </a>{" "}
                      before the {fmtDate(archive.switch?.revert_deadline)} deadline.
                    </>
                  )}
                </p>
              </div>

              {/* Summary stats */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Overdue Follow-ups", value: overdueCount },
                  { label: "Colleges Contacted", value: contactedCount },
                  { label: "Emails Archived",    value: emailCount    },
                ].map(stat => (
                  <div key={stat.label} className="bg-slate-50 rounded-lg p-3 text-center">
                    <p className="text-xl font-black text-slate-900">{stat.value}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Colleges list */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                  <BookMarked className="w-3.5 h-3.5" /> Colleges ({archive.tracked_colleges?.length || 0})
                </p>
                <div className="divide-y divide-slate-50 border border-slate-100 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                  {(archive.tracked_colleges || []).length === 0 ? (
                    <div className="py-6 text-center text-slate-400 text-xs">No colleges in this snapshot</div>
                  ) : archive.tracked_colleges.map(t => {
                    const fuDate   = (t.follow_up_date || "").slice(0, 10);
                    const isOverdue = fuDate && fuDate < today;
                    return (
                      <div key={t.id} className="flex items-center justify-between px-4 py-2.5">
                        <div className="flex-1 min-w-0">
                          <span className="text-sm font-semibold text-slate-800 truncate block">{t.college?.name || t.college_id}</span>
                          {fuDate && (
                            <span className={`text-xs ${isOverdue ? "text-red-500" : "text-slate-400"}`}>
                              Follow-up: {fuDate}{isOverdue ? " · overdue" : ""}
                            </span>
                          )}
                        </div>
                        <span className="text-xs bg-slate-100 text-slate-600 font-bold px-2 py-0.5 rounded-full capitalize ml-3 flex-shrink-0">
                          {t.status || "—"}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Emails list */}
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" /> Correspondence ({archive.emails?.length || 0})
                </p>
                <div className="divide-y divide-slate-50 border border-slate-100 rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                  {(archive.emails || []).length === 0 ? (
                    <div className="py-6 text-center text-slate-400 text-xs">No emails in this snapshot</div>
                  ) : archive.emails.map(e => (
                    <div key={e.id} className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-sm text-slate-700 truncate">{e.subject || "(No subject)"}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-bold flex-shrink-0 ml-2 ${
                        e.direction === "sent" ? "bg-orange-100 text-orange-600" : "bg-green-100 text-green-600"
                      }`}>
                        {e.direction}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── 2. Switch Log (all switches, including reverted) ─────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="switch-history-log">
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100">
          <History className="w-4 h-4 text-slate-400" />
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">Year Switch Log</h3>
          <span className="ml-auto text-xs text-slate-400">{allSwitches.length} switch{allSwitches.length !== 1 ? "es" : ""}</span>
        </div>

        <div className="divide-y divide-slate-50">
          {allSwitches.map((s, i) => (
            <div
              key={s.id}
              data-testid={`switch-log-row-${s.id}`}
              className="flex items-center gap-4 px-5 py-3.5"
            >
              {/* Timeline dot */}
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${s.reverted ? "bg-green-400" : s.revert_eligible ? "bg-orange-400" : "bg-slate-300"}`} />

              {/* Direction */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-sm font-bold text-slate-700">{s.old_target_year}</span>
                <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-sm font-bold text-slate-700">{s.new_target_year}</span>
              </div>

              {/* Date */}
              <span className="text-xs text-slate-400 flex-1">{fmtDate(s.switched_at)}</span>

              {/* Status */}
              <div className="flex flex-col items-end gap-0.5">
                {statusBadge(s)}
                {s.reverted && s.reverted_at && (
                  <span className="text-xs text-slate-400">
                    {fmtDate(s.reverted_at)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
